import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import PageHeader from './components/PageHeader';
import DataTable from './components/DataTable';
import './ContentPage.css';
import FormModal from './components/FormModal';
import DashboardLoader from './components/DashboardLoader';
import InlineEdit from '../../components/shared/InlineEdit';
import {
  productsAPI, servicesAPI, testimonialsAPI, teamAPI,
  careersAPI, faqsAPI, partnersAPI, statsAPI, howItWorksAPI,
  navigationAPI, companyValuesAPI, socialLinksAPI,
} from '../../services/api';

const CONTENT_CONFIG = {
  products: {
    api: productsAPI,
    title: 'Products',
    subtitle: 'Manage your product portfolio',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
    columns: [
      { key: 'name', label: 'Name', sortable: true, editable: true },
      { key: 'tagline', label: 'Tagline', editable: true },
      { key: 'category', label: 'Category', sortable: true, editable: true },
      { key: 'status', label: 'Status', sortable: true, editable: true, render: (val) => <StatusBadge status={val} /> },
      { key: 'url', label: 'URL', editable: true },
    ],
    fields: [
      { key: 'name', label: 'Product Name', required: true },
      { key: 'tagline', label: 'Tagline', required: true },
      { key: 'category', label: 'Category', required: true, type: 'combobox', optionsFrom: 'category', defaults: ['Web App', 'Mobile App', 'API', 'Platform', 'Tool', 'Library'] },
      { key: 'status', label: 'Status', required: true, type: 'select', options: ['live', 'development', 'planned'] },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'url', label: 'URL' },
      { key: 'color', label: 'Color', type: 'color' },
      { key: 'icon', label: 'Icon (emoji)' },
      { key: 'tags', label: 'Tags (comma-separated)' },
      { key: 'sort_order', label: 'Sort Order', type: 'number', default: 0 },
    ],
  },
  services: {
    api: servicesAPI,
    title: 'Services',
    subtitle: 'Manage your service offerings',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>,
    columns: [
      { key: 'title', label: 'Title', sortable: true, editable: true },
      { key: 'description', label: 'Description', editable: true },
      { key: 'icon', label: 'Icon', editable: true },
    ],
    fields: [
      { key: 'title', label: 'Service Title', required: true },
      { key: 'description', label: 'Description', type: 'textarea', required: true },
      { key: 'icon', label: 'Icon' },
      { key: 'sort_order', label: 'Sort Order', type: 'number', default: 0 },
    ],
  },
  team: {
    api: teamAPI,
    title: 'Team',
    subtitle: 'Manage team members',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    columns: [
      { key: 'name', label: 'Name', sortable: true, editable: true },
      { key: 'role', label: 'Role', sortable: true, editable: true },
      { key: 'description', label: 'Description', editable: true },
    ],
    fields: [
      { key: 'name', label: 'Full Name', required: true },
      { key: 'role', label: 'Role', required: true },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'avatar', label: 'Avatar (emoji or initials)' },
      { key: 'sort_order', label: 'Sort Order', type: 'number', default: 0 },
    ],
  },
  testimonials: {
    api: testimonialsAPI,
    title: 'Testimonials',
    subtitle: 'Manage client testimonials',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>,
    columns: [
      { key: 'name', label: 'Name', sortable: true, editable: true },
      { key: 'role', label: 'Role', editable: true },
      { key: 'text', label: 'Testimonial', editable: true },
      { key: 'rating', label: 'Rating', sortable: true, editable: true, render: (val) => <RatingStars rating={val} /> },
    ],
    fields: [
      { key: 'name', label: 'Client Name', required: true },
      { key: 'role', label: 'Role/Company', required: true },
      { key: 'location', label: 'Location' },
      { key: 'text', label: 'Testimonial', type: 'textarea', required: true },
      { key: 'rating', label: 'Rating', type: 'number', min: 1, max: 5, default: 5 },
    ],
  },
  careers: {
    api: careersAPI,
    title: 'Careers',
    subtitle: 'Manage job postings',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>,
    columns: [
      { key: 'title', label: 'Title', sortable: true, editable: true },
      { key: 'department', label: 'Department', sortable: true, editable: true },
      { key: 'location', label: 'Location', sortable: true, editable: true },
      { key: 'type', label: 'Type', sortable: true, editable: true, render: (val) => <TypeBadge type={val} /> },
      { key: 'is_active', label: 'Active', sortable: true, editable: true, render: (val) => <ActiveBadge active={val} /> },
    ],
    fields: [
      { key: 'title', label: 'Job Title', required: true },
      { key: 'department', label: 'Department', required: true },
      { key: 'location', label: 'Location', required: true },
      { key: 'type', label: 'Type', required: true, type: 'select', options: ['Full-time', 'Part-time', 'Contract', 'Remote', 'Internship'] },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'is_active', label: 'Active', type: 'select', options: ['true', 'false'], default: 'true' },
    ],
  },
  faqs: {
    api: faqsAPI,
    title: 'FAQs',
    subtitle: 'Manage frequently asked questions',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
    columns: [
      { key: 'question', label: 'Question', sortable: true, editable: true },
      { key: 'answer', label: 'Answer', editable: true },
    ],
    fields: [
      { key: 'question', label: 'Question', required: true },
      { key: 'answer', label: 'Answer', type: 'textarea', required: true },
      { key: 'sort_order', label: 'Sort Order', type: 'number', default: 0 },
    ],
  },
  stats: {
    api: statsAPI,
    title: 'Stats',
    subtitle: 'Manage display statistics',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>,
    columns: [
      { key: 'value', label: 'Value', sortable: true, editable: true },
      { key: 'label', label: 'Label', sortable: true, editable: true },
    ],
    fields: [
      { key: 'value', label: 'Value', required: true },
      { key: 'label', label: 'Label', required: true },
      { key: 'sort_order', label: 'Sort Order', type: 'number', default: 0 },
    ],
  },
  partners: {
    api: partnersAPI,
    title: 'Partners',
    subtitle: 'Manage partner organizations',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z"/></svg>,
    columns: [
      { key: 'name', label: 'Name', sortable: true, editable: true },
      { key: 'short', label: 'Short Name', editable: true },
    ],
    fields: [
      { key: 'name', label: 'Partner Name', required: true },
      { key: 'short', label: 'Short Name', required: true },
      { key: 'sort_order', label: 'Sort Order', type: 'number', default: 0 },
    ],
  },
  'how-it-works': {
    api: howItWorksAPI,
    title: 'How It Works',
    subtitle: 'Manage how-it-works steps',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
    columns: [
      { key: 'step', label: 'Step', sortable: true, editable: true },
      { key: 'title', label: 'Title', sortable: true, editable: true },
      { key: 'description', label: 'Description', editable: true },
    ],
    fields: [
      { key: 'step', label: 'Step Number', type: 'number', required: true, default: 1 },
      { key: 'title', label: 'Title', required: true },
      { key: 'description', label: 'Description', type: 'textarea', required: true },
    ],
  },
  'company-values': {
    api: companyValuesAPI,
    title: 'Company Values',
    subtitle: 'Manage your core company values',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
    columns: [
      { key: 'title', label: 'Title', sortable: true, editable: true },
      { key: 'description', label: 'Description', editable: true },
      { key: 'sort_order', label: 'Order', sortable: true, editable: true },
    ],
    fields: [
      { key: 'title', label: 'Value Title', required: true },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'sort_order', label: 'Sort Order', type: 'number', default: 0 },
    ],
  },
  'social-links': {
    api: socialLinksAPI,
    title: 'Social Links',
    subtitle: 'Manage social media links',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
    columns: [
      { key: 'platform', label: 'Platform', sortable: true, editable: true },
      { key: 'url', label: 'URL', sortable: true, editable: true },
    ],
    fields: [
      { key: 'platform', label: 'Platform (e.g. twitter, github, linkedin)', required: true },
      { key: 'url', label: 'URL', required: true },
    ],
  },
  navigation: {
    api: navigationAPI,
    title: 'Navigation',
    subtitle: 'Manage site navigation links',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
    columns: [
      { key: 'label', label: 'Label', sortable: true, editable: true },
      { key: 'path', label: 'Path', sortable: true, editable: true },
      { key: 'sort_order', label: 'Order', sortable: true, editable: true },
      { key: 'is_visible', label: 'Visible', sortable: true, editable: true, render: (val) => <VisibilityBadge visible={val} /> },
      { key: 'requires_auth', label: 'Auth Required', sortable: true, editable: true, render: (val) => <AuthBadge required={val} /> },
    ],
    fields: [
      { key: 'label', label: 'Link Label', required: true },
      { key: 'path', label: 'URL Path (e.g. /about)', required: true },
      { key: 'sort_order', label: 'Sort Order', type: 'number', default: 0 },
      { key: 'is_visible', label: 'Visible', type: 'select', options: ['true', 'false'], default: 'true' },
      { key: 'requires_auth', label: 'Requires Auth', type: 'select', options: ['true', 'false'], default: 'false' },
    ],
  },
};

function VisibilityBadge({ visible }) {
  const isVisible = visible === true || visible === 'true';
  return (
    <span style={{
      background: isVisible ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
      color: isVisible ? '#10b981' : '#ef4444',
      padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap'
    }}>
      {isVisible ? '● Visible' : '○ Hidden'}
    </span>
  );
}

function AuthBadge({ required }) {
  const isRequired = required === true || required === 'true';
  return (
    <span style={{
      background: isRequired ? 'rgba(245, 158, 11, 0.1)' : 'rgba(107, 114, 128, 0.1)',
      color: isRequired ? '#f59e0b' : '#6b7280',
      padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap'
    }}>
      {isRequired ? '🔒 Yes' : '🌐 No'}
    </span>
  );
}

function ActiveBadge({ active }) {
  const isActive = active === true || active === 'true';
  return (
    <span style={{
      background: isActive ? 'rgba(16, 185, 129, 0.1)' : 'rgba(107, 114, 128, 0.1)',
      color: isActive ? '#10b981' : '#6b7280',
      padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap'
    }}>
      {isActive ? '● Active' : '○ Inactive'}
    </span>
  );
}

function StatusBadge({ status }) {
  const colors = {
    live: { bg: 'rgba(16, 185, 129, 0.1)', color: '#10b981', label: '● Live' },
    development: { bg: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b', label: '● Dev' },
    planned: { bg: 'rgba(107, 114, 128, 0.1)', color: '#6b7280', label: '○ Planned' },
  };
  const c = colors[status] || colors.planned;
  return (
    <span style={{ background: c.bg, color: c.color, padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
      {c.label}
    </span>
  );
}

function TypeBadge({ type }) {
  return (
    <span style={{ background: 'var(--bg-surface)', color: 'var(--text-secondary)', padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
      {type}
    </span>
  );
}

function RatingStars({ rating }) {
  const stars = Number(rating) || 0;
  return (
    <span style={{ color: '#f59e0b', letterSpacing: '0.1em' }}>
      {'★'.repeat(Math.min(stars, 5))}{'☆'.repeat(Math.max(5 - stars, 0))}
    </span>
  );
}

export default function ContentPage() {
  const { section } = useParams();
  const { isEditor } = useAuth();
  const { refresh } = useData();
  const navigate = useNavigate();
  const config = CONTENT_CONFIG[section];

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | { edit: item }
  const [detailItem, setDetailItem] = useState(null);

  const fetchData = useCallback(async () => {
    if (!config) return;
    setLoading(true);
    try {
      const res = await config.api.list();
      setItems(res.data || []);
    } catch (err) {
      console.error(`Failed to fetch ${section}:`, err);
    } finally {
      setLoading(false);
    }
  }, [config, section]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!config) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>
        <p>Section not found</p>
        <button onClick={() => navigate('/dashboard')} className="btn-action btn-action--outline" style={{ marginTop: '1rem' }}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  const handleCreate = async (formData) => {
    try {
      if (section === 'products' && typeof formData.tags === 'string') {
        formData.tags = formData.tags.split(',').map((t) => t.trim()).filter(Boolean);
      }
      // Convert string booleans for navigation
      if (section === 'navigation') {
        formData.is_visible = formData.is_visible === 'true' || formData.is_visible === true;
        formData.requires_auth = formData.requires_auth === 'true' || formData.requires_auth === true;
        formData.sort_order = Number(formData.sort_order) || 0;
      }
      if (section === 'careers' && formData.is_active !== undefined) {
        formData.is_active = formData.is_active === 'true' || formData.is_active === true;
      }
      // Coerce numeric sort_order / step / rating
      ['sort_order', 'step', 'rating'].forEach((k) => {
        if (formData[k] !== undefined && formData[k] !== '') formData[k] = Number(formData[k]);
      });
      await config.api.create(formData);
      setModal(null);
      fetchData();
      refresh();
    } catch (err) {
      alert('Create failed: ' + (err.response?.data?.detail || err.message));
      throw err;
    }
  };

  const handleInlineSave = async (id, field, value) => {
    try {
      // Convert string booleans for navigation inline edits
      let parsedValue = value;
      if (section === 'navigation' && (field === 'is_visible' || field === 'requires_auth')) {
        parsedValue = value === 'true' || value === true;
      }
      if (section === 'careers' && field === 'is_active') {
        parsedValue = value === 'true' || value === true;
      }
      if (field === 'sort_order' || field === 'step' || field === 'rating') {
        parsedValue = Number(value) || 0;
      }
      await config.api.patch(id, field, parsedValue);
      fetchData();
      refresh();
    } catch (err) {
      alert('Update failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete this ${config.title.toLowerCase().slice(0, -1)}?`)) return;
    try {
      await config.api.delete(item.id);
      fetchData();
      refresh();
    } catch (err) {
      alert('Delete failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const tableColumns = config.columns.map((col) => ({
    ...col,
    render: col.editable && isEditor
      ? (val, item) => (
          <InlineEdit
            value={String(val ?? '')}
            editable={true}
            onSave={(newVal) => handleInlineSave(item.id, col.key, newVal)}
          />
        )
      : col.render || undefined,
  }));

  return (
    <div>
      <PageHeader
        title={config.title}
        subtitle={config.subtitle}
        icon={config.icon}
        backTo="/dashboard"
        actions={
          isEditor && (
            <button className="btn-action btn-action--primary" onClick={() => setModal('create')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add New
            </button>
          )
        }
      />

      {loading ? (
        <DashboardLoader text={`Loading ${config.title.toLowerCase()}...`} />
      ) : (
        <DataTable
          columns={tableColumns}
          data={items}
          onRowClick={(item) => setDetailItem(item)}
          searchPlaceholder={`Search ${config.title.toLowerCase()}...`}
          emptyMessage={`No ${config.title.toLowerCase()} found. Click "Add New" to create one.`}
          actions={isEditor ? (item) => (
            <>
              <button
                className="btn-action btn-action--danger"
                style={{ padding: '0.3rem 0.75rem', fontSize: '0.75rem' }}
                onClick={() => handleDelete(item)}
              >
                Delete
              </button>
            </>
          ) : undefined}
        />
      )}

      {/* Create Modal */}
      {modal === 'create' && (
        <FormModal
          title={`Create ${config.title.slice(0, -1)}`}
          fields={config.fields}
          existingItems={items}
          onSave={handleCreate}
          onClose={() => setModal(null)}
        />
      )}

      {/* Detail Slide-Over */}
      {detailItem && (
        <DetailPanel
          item={detailItem}
          config={config}
          section={section}
          onClose={() => setDetailItem(null)}
          isEditor={isEditor}
          onInlineSave={handleInlineSave}
          onDelete={() => {
            handleDelete(detailItem);
            setDetailItem(null);
          }}
        />
      )}
    </div>
  );
}

function DetailPanel({ item, config, section, onClose, isEditor, onInlineSave, onDelete }) {
  const [closing, setClosing] = useState(false);
  const navigate = useNavigate();

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 250);
  };

  const handleOpenOverview = () => {
    onClose();
    navigate(`/dashboard/${section}/${item.id}`);
  };

  return (
    <>
      <div
        className={`detail-overlay ${closing ? 'detail-overlay--exit' : ''}`}
        onClick={handleClose}
      />
      <div className={`detail-panel ${closing ? 'detail-panel--exit' : ''}`}>
        <div className="detail-panel__header">
          <h2 className="detail-panel__title">Details</h2>
          <button className="detail-panel__close" onClick={handleClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div className="detail-panel__body">
          {config.columns.map((col) => (
            <div key={col.key} className="detail-panel__row">
              <span className="detail-panel__label">{col.label}</span>
              <span className="detail-panel__value">
                {col.editable && isEditor ? (
                  <InlineEdit
                    value={String(item[col.key] ?? '')}
                    editable={true}
                    onSave={(newVal) => onInlineSave(item.id, col.key, newVal)}
                  />
                ) : (
                  String(item[col.key] ?? '—')
                )}
              </span>
            </div>
          ))}

          {/* Show extra fields not in columns */}
          {Object.entries(item).filter(([k]) => !config.columns.find((c) => c.key === k) && k !== 'id' && k !== 'created_at' && k !== 'updated_at').map(([key, val]) => (
            <div key={key} className="detail-panel__row">
              <span className="detail-panel__label">{key}</span>
              <span className="detail-panel__value">{typeof val === 'object' ? JSON.stringify(val) : String(val ?? '—')}</span>
            </div>
          ))}

          {item.created_at && (
            <div className="detail-panel__row">
              <span className="detail-panel__label">Created</span>
              <span className="detail-panel__value">{new Date(item.created_at).toLocaleString()}</span>
            </div>
          )}
        </div>
        {isEditor && (
          <div className="detail-panel__footer">
            <button className="btn-action btn-action--outline" onClick={handleOpenOverview}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
              Overview
            </button>
            <button className="btn-action btn-action--danger" onClick={onDelete}>
              Delete
            </button>
          </div>
        )}
      </div>
    </>
  );
}
